import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler, getErrorMessage } from '@zero/shared/util-global-state';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { TagsInfrastructure } from '../infrastructure/tags.infrastructure';
import { tagsActions, tagsApiActions } from './tags.actions';
import { tagsFeature } from './tags.reducer';
import { selectTags } from './tags.selectors';

@Injectable({ providedIn: 'root' })
export class TagsEffects {
  readonly #store = inject(Store);
  readonly #actions$ = inject(Actions);
  readonly #infra = inject(TagsInfrastructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public loadTags$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.loadTags),
      switchMap(({ tagType }) =>
        this.#infra.getTags(tagType).pipe(
          map((response) => {
            switch (tagType) {
              case 'position':
                return tagsApiActions.loadPositionTagsSuccess({ tags: response });
              case 'watchlist':
                return tagsApiActions.loadWatchlistTagsSuccess({ tags: response });
              case 'savings':
                throw new Error('Not implemented');
              default:
                throw new Error(`Unknown tag type: ${tagType}`);
            }
          }),
          catchError((error) => of(tagsApiActions.loadTagsFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public presetActiveTags$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsApiActions.loadPositionTagsSuccess, tagsApiActions.loadWatchlistTagsSuccess, tagsApiActions.loadSavingsTagsSuccess),
      concatLatestFrom(() => this.#store.select(tagsFeature.selectTagType)),
      concatLatestFrom(() => this.#store.select(selectTags)),
      switchMap(([[, tagType], tags]) =>
        this.#infra.getActivatedTags(tagType, tags).pipe(
          tap((tagIds) => this.#infra.cache(tagType, tagIds)),
          map((tagIds) => tagsActions.presetActivatedTags({ tagIds })),
        ),
      ),
    );
  });

  public deleteTag$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.deleteTag),
      switchMap(({ tagId }) =>
        this.#infra.deleteTag(tagId).pipe(
          map(() => tagsApiActions.deleteTagSuccess({ tagId })),
          catchError((error) => of(tagsApiActions.deleteTagFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public removeActiveTag$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsApiActions.deleteTagSuccess),
      concatLatestFrom(() => this.#store.select(tagsFeature.selectTagType)),
      switchMap(([{ tagId }, tagType]) =>
        this.#infra.removeActiveTag(tagType, tagId).pipe(
          tap((tagIds) => this.#infra.cache(tagType, tagIds)),
          map((tagIds) => tagsActions.presetActivatedTags({ tagIds })),
        ),
      ),
    );
  });

  public resetTags$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.resetTags),
      concatLatestFrom(() => this.#store.select(tagsFeature.selectTagType)),
      switchMap(([, tagType]) =>
        this.#infra.resetTags().pipe(
          tap((tagIds) => this.#infra.cache(tagType, tagIds)),
          map((tagIds) => tagsActions.presetActivatedTags({ tagIds })),
        ),
      ),
    );
  });

  public toggleTag$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.toggleTag),
      concatLatestFrom(() => this.#store.select(tagsFeature.selectTagType)),
      switchMap(([{ tagId }, tagType]) =>
        this.#infra.toggleTag(tagType, tagId).pipe(
          tap((tagIds) => this.#infra.cache(tagType, tagIds)),
          map((tagIds) => tagsActions.presetActivatedTags({ tagIds })),
        ),
      ),
    );
  });

  public activateAllTags$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.activateAllTags),
      concatLatestFrom(() => this.#store.select(tagsFeature.selectTagType)),
      concatLatestFrom(() => this.#store.select(selectTags)),
      switchMap(([[, tagType], tags]) =>
        this.#infra.activateAllTags(tags).pipe(
          tap((tagIds) => this.#infra.cache(tagType, tagIds)),
          map((tagIds) => tagsActions.presetActivatedTags({ tagIds })),
        ),
      ),
    );
  });

  public deleteLinkedTag$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.deleteLinkedTag),
      switchMap(({ tagId, entityId }) =>
        this.#infra.deleteLinkedTag(tagId, entityId).pipe(
          map(() => tagsApiActions.deleteLinkedTagSuccess({ tagId, entityId })),
          catchError((error) => of(tagsApiActions.deleteLinkedTagFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public addTag$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(tagsActions.addTag),
      concatLatestFrom(() => this.#store.select(tagsFeature.selectTagType)),
      switchMap(([{ tagId, entityId, name }, tagType]) =>
        this.#infra.addTag(tagId, entityId, name, tagType).pipe(
          map((tag) => tagsApiActions.addTagSuccess({ tag })),
          catchError((error) => of(tagsApiActions.addTagFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(tagsApiActions.loadTagsFailure, tagsApiActions.deleteTagFailure),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
